import { useState, useEffect } from 'react';
import {
  pricingServices,
  contactInfo,
  services,
  features,
  petSizes,
  socialLinks,
  googleTag,
  comments,
  reviewLinks
} from '../data/mockData';
import { Service, ContactInfo, SocialLink, Feature, PetSize, GoogleTag, Comment, ReviewLinks } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT?.replace('/api/send-email', '') || 'https://premium-pets-mail.vercel.app';

// Global cache to avoid multiple API calls
let serverDataCache: {
  pricingServices: any;
  services: Service[];
  contactInfo: ContactInfo;
  features: Feature[];
  petSizes: PetSize[];
  socialLinks: SocialLink[];
  googleTag: GoogleTag;
  comments: Comment[];
  reviewLinks: ReviewLinks;
} | null = null;

let serverDataPromise: Promise<void> | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 1 minute cache

// Function to clear cache (called when data is updated)
export const clearAdminDataCache = () => {
  serverDataCache = null;
  lastFetchTime = 0;
};

// Load all admin data from server, then localStorage, fallback to mockData
const loadAdminData = (): {
  pricingServices: any;
  services: Service[];
  contactInfo: ContactInfo;
  features: Feature[];
  petSizes: PetSize[];
  socialLinks: SocialLink[];
  googleTag: GoogleTag;
  comments: Comment[];
  reviewLinks: ReviewLinks;
} => {
  // For initial load, use localStorage or mockData
  // Server data will be loaded async in useEffect
  const allData = localStorage.getItem('admin_all_data');
  if (allData) {
    try {
      const parsed = JSON.parse(allData);
      return {
        pricingServices: parsed.pricingServices || pricingServices,
        services: parsed.services || services,
        contactInfo: parsed.contactInfo || contactInfo,
        features: parsed.features || features,
        petSizes: parsed.petSizes || petSizes,
        socialLinks: parsed.socialLinks || socialLinks,
        googleTag: parsed.googleTag || googleTag,
        comments: parsed.comments || comments,
        reviewLinks: parsed.reviewLinks || reviewLinks,
      };
    } catch (e) {
      console.error('Error loading admin data from localStorage:', e);
    }
  }
  
  // Fallback to mockData
  return {
    pricingServices,
    services,
    contactInfo,
    features,
    petSizes,
    socialLinks,
    googleTag,
    comments,
    reviewLinks,
  };
};

// Async load from server - SINGLE CALL with cache
const loadAdminDataFromServer = async (): Promise<{
  pricingServices: any;
  services: Service[];
  contactInfo: ContactInfo;
  features: Feature[];
  petSizes: PetSize[];
  socialLinks: SocialLink[];
  googleTag: GoogleTag;
  comments: Comment[];
  reviewLinks: ReviewLinks;
} | null> => {
  // Return cached data if available and fresh
  const now = Date.now();
  if (serverDataCache && (now - lastFetchTime) < CACHE_DURATION) {
    return serverDataCache;
  }

  // If already fetching, wait for existing promise
  if (serverDataPromise) {
    await serverDataPromise;
    return serverDataCache;
  }

  // Create new fetch promise
  serverDataPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-data`);
      const result = await response.json();
      
      if (result.success && result.data) {
        // Save to localStorage for offline access
        localStorage.setItem('admin_all_data', JSON.stringify(result.data));
        
        // Update cache
        serverDataCache = {
          pricingServices: result.data.pricingServices || pricingServices,
          services: result.data.services || services,
          contactInfo: result.data.contactInfo || contactInfo,
          features: result.data.features || features,
          petSizes: result.data.petSizes || petSizes,
          socialLinks: result.data.socialLinks || socialLinks,
          googleTag: result.data.googleTag || googleTag,
          comments: result.data.comments || comments,
          reviewLinks: result.data.reviewLinks || reviewLinks,
        };
        lastFetchTime = Date.now();
      }
    } catch (e) {
      console.error('Error loading admin data from server:', e);
    } finally {
      serverDataPromise = null;
    }
  })();

  await serverDataPromise;
  return serverDataCache;
};

export const usePricingServices = () => {
  const [data, setData] = useState(loadAdminData().pricingServices);

  useEffect(() => {
    // Load from server only if no localStorage data or cache expired
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      // Only fetch from server if no local data
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.pricingServices);
        }
      });
    }
    
    const updateData = () => {
      // Clear cache when data is updated
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.pricingServices);
    };
    
    // Listen for storage changes
    window.addEventListener('storage', updateData);
    // Custom event for same-tab updates
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useServices = () => {
  const [data, setData] = useState<Service[]>(loadAdminData().services);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.services);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.services);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useContactInfo = () => {
  const [data, setData] = useState<ContactInfo>(loadAdminData().contactInfo);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.contactInfo);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.contactInfo);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useFeatures = () => {
  const [data, setData] = useState<Feature[]>(loadAdminData().features);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.features);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.features);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const usePetSizes = () => {
  const [data, setData] = useState<PetSize[]>(loadAdminData().petSizes);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.petSizes);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.petSizes);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useSocialLinks = () => {
  const [data, setData] = useState<SocialLink[]>(loadAdminData().socialLinks);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.socialLinks);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.socialLinks);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useGoogleTag = () => {
  const [data, setData] = useState<GoogleTag>(loadAdminData().googleTag);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.googleTag);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.googleTag);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useComments = () => {
  const [data, setData] = useState<Comment[]>(loadAdminData().comments);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.comments);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.comments);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

export const useReviewLinks = () => {
  const [data, setData] = useState<ReviewLinks>(loadAdminData().reviewLinks);

  useEffect(() => {
    const hasLocalData = localStorage.getItem('admin_all_data');
    
    if (!hasLocalData) {
      loadAdminDataFromServer().then((serverData) => {
        if (serverData) {
          setData(serverData.reviewLinks);
        }
      });
    }
    
    const updateData = () => {
      clearAdminDataCache();
      const localData = loadAdminData();
      setData(localData.reviewLinks);
    };
    
    window.addEventListener('storage', updateData);
    window.addEventListener('admin-data-updated', updateData);
    
    return () => {
      window.removeEventListener('storage', updateData);
      window.removeEventListener('admin-data-updated', updateData);
    };
  }, []);

  return data;
};

